import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { DateToLocale, ToFirstCapital } from "../../Utils/helpers";
import { viewLeadById, changeLeadStatus } from "../../Redux/Actions/leadActions";
import MainLayout from "../../Layout/mainLayout";
import toast from "react-hot-toast";
import "./viewLead.css";

const ViewLead = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [leadData, setLeadData] = useState(null);
    const [pageLoading, setPageLoading] = useState(false);
    const [statusValue, setStatusValue] = useState("");
    const [statusUpdating, setStatusUpdating] = useState(false);
    const [cancelReason, setCancelReason] = useState("");
    const [showCancelReason, setShowCancelReason] = useState(false);

    useEffect(() => {
        if (leadData?.status) {
            setStatusValue(leadData.status);
        }
    }, [leadData]);

    const getStatusOptions = (currentStatus) => {
        switch (currentStatus) {
            case "pending":
                return [
                    { value: "verified", label: "Verified" },
                    { value: "cancelled", label: "Cancelled" },
                ];
            case "verified":
                return [{ value: "closed", label: "Closed" }];
            case "scheduled":
                return [{ value: "closed", label: "Closed" }];
            case "cancelled":
                return [];
            default:
                return [
                    { value: "pending", label: "Pending" },
                    { value: "verified", label: "Verified" },
                    { value: "closed", label: "Closed" },
                    { value: "cancelled", label: "Cancelled" },
                    { value: "scheduled", label: "Scheduled" },
                ];
        }
    };

    const handleStatusSelect = (e) => {
        const value = e.target.value;
        setStatusValue(value);
        if (value === "cancelled") {
            setShowCancelReason(true);
        } else {
            setShowCancelReason(false);
            setCancelReason("");
        }
    };

    const handleStatusUpdate = async () => {
        if (!statusValue) return toast.error("Please select status");
        if (statusValue === "cancelled" && !cancelReason.trim()) {
            return toast.error("Please enter a reason for cancellation");
        }

        try {
            setStatusUpdating(true);
            const payload = { lead_id: id, status: statusValue };
            if (statusValue === "cancelled") {
                payload.reason = cancelReason;
            }
            const res = await dispatch(changeLeadStatus(payload));
            if (res?.statusCode === 200) {
                setLeadData((prev) => ({ ...prev, status: statusValue, reason: payload.reason || prev.reason }));
                setShowCancelReason(false);
                setCancelReason("");
            }
        } catch {
            toast.error("Something went wrong");
        } finally {
            setStatusUpdating(false);
        }
    };

    useEffect(() => {
        const fetchLead = async () => {
            try {
                setPageLoading(true);
                const res = await dispatch(viewLeadById(id));
                if (res && res.statusCode === 200) {
                    setLeadData(res.data);
                }
            } catch {
                // handle error
            } finally {
                setPageLoading(false);
            }
        };
        fetchLead();
    }, [id, dispatch]);

    if (pageLoading) {
        return (
            <MainLayout>
                <div className="vl-loader-wrap">
                    <div className="vl-loader" />
                    <p className="vl-loader-text">Loading lead data…</p>
                </div>
            </MainLayout>
        );
    }

    if (!leadData) {
        return (
            <MainLayout>
                <div className="vl-empty-wrap">
                    <div className="vl-empty-icon">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <circle cx="12" cy="12" r="10" />
                            <path d="M12 8v4M12 16h.01" />
                        </svg>
                    </div>
                    <p className="vl-empty-text">No Lead Data Found.</p>
                    <Link to="/leads" className="vl-back-link">← Back to Leads</Link>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="vl-wrapper">

                <div className="vl-header">
                    <div className="vl-header-left">
                        <Link to="/leads" className="vl-back-btn">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="15 18 9 12 15 6" />
                            </svg>
                            Back
                        </Link>
                        <div className="vl-header-divider" />
                        <div>
                            <div className="vl-breadcrumb">Leads / List / View</div>
                        </div>
                    </div>
                    <div style={{ display: "flex", gap: "10px" }}>
                        <div className={`vl-status-badge vl-status-badge--${leadData?.status?.toLowerCase()}`}>
                            {ToFirstCapital(leadData?.status || "")}
                        </div>
                        {(leadData?.status === "verified") &&
                            <button
                                className="vl-create-appt-btn"
                                onClick={() => navigate(`/leads/create_appointment/${id}`, {
                                    state: { contact_number: leadData?.contact_number }
                                })}
                                type="button"
                            >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="4" width="18" height="18" rx="2" />
                                    <line x1="16" y1="2" x2="16" y2="6" />
                                    <line x1="8" y1="2" x2="8" y2="6" />
                                    <line x1="3" y1="10" x2="21" y2="10" />
                                    <line x1="12" y1="14" x2="12" y2="18" />
                                    <line x1="10" y1="16" x2="14" y2="16" />
                                </svg>
                                Create Appointment
                            </button>
                        }
                    </div>
                </div>

                <div className="vl-hero-card">
                    <div className="vl-hero-left">
                        <div className="vl-hero-avatar">
                            {leadData?.name?.charAt(0)?.toUpperCase() || "L"}
                        </div>
                        <div>
                            <div className="vl-hero-label">Lead Name</div>
                            <div className="vl-hero-value">{leadData?.name || "—"}</div>
                            {leadData?.email && (
                                <div className="vl-hero-sub">{leadData.email}</div>
                            )}
                        </div>
                    </div>
                    <div className="vl-hero-meta">
                        {(leadData?.country_code || leadData?.contact_number) && (
                            <div className="vl-hero-meta-item">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.36 12 19.79 19.79 0 0 1 1.21 3.58 2 2 0 0 1 3.22 1.4h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21 16.92z" />
                                </svg>
                                {leadData?.country_code} {leadData?.contact_number}
                            </div>
                        )}
                        {leadData?.country?.country_name && (
                            <div className="vl-hero-meta-item">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10" />
                                    <line x1="2" y1="12" x2="22" y2="12" />
                                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                                </svg>
                                {leadData.country.country_name}
                            </div>
                        )}
                        <div className="vl-hero-meta-item">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="4" width="18" height="18" rx="2" />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                                <line x1="3" y1="10" x2="21" y2="10" />
                            </svg>
                            {DateToLocale(leadData?.created_at) || "—"}
                        </div>
                    </div>
                </div>

                <div className="vl-grid-layout">

                    <div className="vl-col">

                        <div className="vl-card">
                            <div className="vl-card-header">
                                <span className="vl-card-badge">01</span>
                                <h2 className="vl-card-title">Lead Information</h2>
                            </div>
                            <div className="vl-info-grid">
                                <div className="vl-info-item">
                                    <span className="vl-info-label">Full Name</span>
                                    <span className="vl-info-value">{leadData?.name || "No Data"}</span>
                                </div>
                                <div className="vl-info-item">
                                    <span className="vl-info-label">Email</span>
                                    <span className="vl-info-value">{leadData?.email || "No Data"}</span>
                                </div>
                                <div className="vl-info-item">
                                    <span className="vl-info-label">Contact Number</span>
                                    <span className="vl-info-value">
                                        {leadData?.country_code && leadData?.contact_number
                                            ? `${leadData.country_code} ${leadData.contact_number}`
                                            : leadData?.contact_number || "No Data"}
                                    </span>
                                </div>
                                <div className="vl-info-item">
                                    <span className="vl-info-label">Status</span>
                                    <span className={`vl-inline-status vl-inline-status--${leadData?.status?.toLowerCase()}`}>
                                        {ToFirstCapital(leadData?.status)}
                                    </span>
                                </div>
                                <div className="vl-info-item">
                                    <span className="vl-info-label">Country</span>
                                    <span className="vl-info-value">{leadData?.country?.country_name || "No Data"}</span>
                                </div>
                                <div className="vl-info-item">
                                    <span className="vl-info-label">Currency</span>
                                    <span className="vl-info-value">{leadData?.country?.country_currency || "No Data"}</span>
                                </div>
                                <div className="vl-info-item">
                                    <span className="vl-info-label">Created At</span>
                                    <span className="vl-info-value">{DateToLocale(leadData?.created_at) || "No Data"}</span>
                                </div>
                            </div>
                        </div>

                        {leadData?.location?.coordinates?.length === 2 &&
                            (leadData.location.coordinates[0] !== 0 || leadData.location.coordinates[1] !== 0) && (
                                <div className="vl-card">
                                    <div className="vl-card-header">
                                        <span className="vl-card-badge">05</span>
                                        <h2 className="vl-card-title">Location</h2>
                                    </div>
                                    <div className="vl-coords-row" style={{ marginBottom: 12 }}>
                                        <div className="vl-coord-chip">
                                            <span className="vl-coord-label">LAT</span>
                                            <span className="vl-coord-value">
                                                {leadData.location.coordinates[1].toFixed(6)}
                                            </span>
                                        </div>
                                        <div className="vl-coord-chip">
                                            <span className="vl-coord-label">LNG</span>
                                            <span className="vl-coord-value">
                                                {leadData.location.coordinates[0].toFixed(6)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="vl-map-wrap">
                                        <iframe
                                            title="Lead Location"
                                            className="vl-map-iframe"
                                            loading="lazy"
                                            allowFullScreen
                                            src={`https://maps.google.com/maps?q=${leadData.location.coordinates[1]},${leadData.location.coordinates[0]}&z=15&output=embed`}
                                        />
                                    </div>
                                </div>
                            )}

                    </div>

                    <div className="vl-col">

                        {leadData?.partner && (
                            <div className="vl-card">
                                <div className="vl-card-header">
                                    <span className="vl-card-badge">03</span>
                                    <h2 className="vl-card-title">Partner Details</h2>
                                </div>
                                <div className="vl-info-grid">
                                    {leadData.partner.profile_image && (
                                        <div className="vl-info-item" style={{ gridColumn: "1 / -1" }}>
                                            <img
                                                src={leadData.partner.profile_image}
                                                alt={leadData.partner.name}
                                                style={{
                                                    width: 64,
                                                    height: 64,
                                                    borderRadius: "50%",
                                                    objectFit: "cover",
                                                    border: "2px solid #e5e7eb",
                                                }}
                                            />
                                        </div>
                                    )}
                                    <div className="vl-info-item">
                                        <span className="vl-info-label">Full Name</span>
                                        <span className="vl-info-value">{leadData.partner?.name || "No Data"}</span>
                                    </div>
                                    <div className="vl-info-item">
                                        <span className="vl-info-label">Staff ID</span>
                                        <span className="vl-info-value">{leadData.partner?.staff_id || "No Data"}</span>
                                    </div>
                                    <div className="vl-info-item">
                                        <span className="vl-info-label">Email</span>
                                        <span className="vl-info-value">{leadData.partner?.email || "No Data"}</span>
                                    </div>
                                    <div className="vl-info-item">
                                        <span className="vl-info-label">Contact Number</span>
                                        <span className="vl-info-value">{leadData.partner?.contact_number || "No Data"}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {leadData?.address && (
                            <div className="vl-card">
                                <div className="vl-card-header">
                                    <span className="vl-card-badge">04</span>
                                    <h2 className="vl-card-title">Address</h2>
                                </div>
                                <div className="vl-address-item">
                                    <div className="vl-address-icon">
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
                                            <circle cx="12" cy="10" r="3" />
                                        </svg>
                                    </div>
                                    <div>
                                        <div className="vl-address-label">Full Address</div>
                                        <div className="vl-address-val">{leadData.address}</div>
                                    </div>
                                </div>
                            </div>
                        )}


                        {leadData?.reason && (
                            <div className="vl-card">
                                <div className="vl-card-header">
                                    <span className="vl-card-badge">02</span>
                                    <h2 className="vl-card-title">Reason</h2>
                                </div>
                                <p className="vl-reason-text">{leadData.reason}</p>
                            </div>
                        )}

                        {leadData?.status !== "cancelled" && (
                            <div className="vl-card">
                                <div className="vl-card-header">
                                    <span className="vl-card-badge">02</span>
                                    <h2 className="vl-card-title">Update Status</h2>
                                </div>

                                <div className="vl-status-panel">
                                    <div className="vl-status-field">
                                        <label className="vl-info-label">Select Status</label>
                                        <select
                                            className="vl-status-select"
                                            value={statusValue}
                                            onChange={handleStatusSelect}
                                            disabled={statusUpdating}
                                        >
                                            <option value={leadData?.status}>{ToFirstCapital(leadData?.status)}</option>
                                            {getStatusOptions(leadData?.status).map((opt) => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {showCancelReason && (
                                        <div className="vl-status-field">
                                            <label className="vl-info-label">
                                                Cancellation Reason <span style={{ color: "#ef4444" }}>*</span>
                                            </label>
                                            <textarea
                                                className="vl-status-textarea"
                                                value={cancelReason}
                                                onChange={(e) => setCancelReason(e.target.value)}
                                                placeholder="Enter the reason for cancellation..."
                                                rows={3}
                                                disabled={statusUpdating}
                                            />
                                        </div>
                                    )}
                                    {!(leadData?.status === "closed") &&
                                        <button
                                            type="button"
                                            className="vl-status-btn"
                                            onClick={handleStatusUpdate}
                                            disabled={statusUpdating || !statusValue || statusValue === leadData?.status}
                                        >
                                            {statusUpdating ? "Updating..." : "Update Status"}
                                        </button>
                                    }
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default ViewLead;